/* The workbench reads purchase state; media controls never select a variant. */
(() => {
  'use strict';
  const all = (s, r = document) => [...r.querySelectorAll(s)];
  const visible = el => el && !el.hidden && !el.closest('[hidden]');
  function mount(root) {
    if (root.dataset.explorerReady) return;
    root.dataset.explorerReady = 'true';
    root.classList.add('explorer--ready');
    const purchase = root.closest('[data-product-state]');
    const groups = all('[data-explorer-variant]', root);
    const modes = all('[data-explorer-mode]', root);
    const films = all('[data-pdp-video]', root);
    const expand = root.querySelector('[data-explorer-expand]');
    const english = document.documentElement.lang === 'en';
    let current = groups.find(g => !g.hidden), partId = null, mode = 'appearance', sceneIndex = 0, expandFocus;
    const colours = purchase?.querySelector('.pdp-colours');
    if (colours && window.matchMedia) {
      const home = document.createComment('Colour control home'); colours.after(home);
      const phone = window.matchMedia('(max-width: 760px)');
      const place = () => phone.matches ? root.querySelector('[data-explorer-mobile-colours]').append(colours) : home.parentNode.insertBefore(colours, home);
      phone.addEventListener('change', place); place();
    }
    films.forEach(f => { f.dataset.poster = f.innerHTML; });
    const panels = group => group ? all('[data-explorer-panel]', group) : [];
    const usable = p => p.dataset.hasImage === 'true' && !p.querySelector('.explorer-detail-image[data-failed]');
    const candidates = category => panels(current).filter(p => usable(p) && p.dataset.category === category);
    const viewList = group => {
      if (!group) return [];
      const views = all('[data-explorer-view]', group).filter(v => v.dataset.failed !== 'true');
      if (!['structure', 'storage'].includes(mode)) return views;
      const parts = panels(group).filter(p => usable(p) && p.dataset.category === mode);
      return views.filter(v => parts.some(p => p.dataset.viewId === v.dataset.explorerView));
    };
    const sceneList = () => {
      const group = all('[data-explorer-scenes]', root).find(g => g.dataset.explorerScenes === current?.dataset.explorerVariant);
      return group ? all('.workbench-scene', group).filter(f => f.dataset.failed !== 'true') : [];
    };
    const film = () => films.find(f => f.dataset.explorerFilm === current?.dataset.explorerVariant && f.dataset.failed !== 'true');
    function hasMode(value) {
      if (value === 'structure' || value === 'storage') return candidates(value).length > 0;
      if (value === 'scenes') return sceneList().length > 0;
      if (value === 'video') return !!film();
      return value === 'appearance'; // The gallery supplies a readable same-colour empty state.
    }
    function stopFilms() {
      films.forEach(f => {
        const v = f.querySelector('video');
        if (v) { v.pause(); v.removeAttribute('src'); all('source', v).forEach(s => s.removeAttribute('src')); v.load(); f.innerHTML = f.dataset.poster; }
      });
    }
    function repairFocus(previous) {
      if (!previous || previous === document.body || visible(previous)) return;
      const target = all('[data-explorer-part][aria-selected="true"]', root).find(visible)
        || modes.find(b => b.dataset.explorerMode === mode && visible(b))
        || root.querySelector('[data-gallery-stage]');
      target?.focus({preventScroll:true});
    }
    function location(group, show) {
      const button = group?.querySelector('[data-explorer-location]');
      if (!button) return;
      show = show && !button.hidden;
      group.classList.toggle('explorer-show-location', show);
      button.setAttribute('aria-pressed', String(show));
      button.textContent = english ? (show ? 'Back to detail' : 'Show on pack') : (show ? 'Zur Detailansicht' : 'Am Rucksack zeigen');
    }
    function view(group, id) {
      const available = viewList(group), selected = available.find(v => v.dataset.explorerView === id) || available[0];
      all('[data-explorer-view]', group).forEach(v => { v.hidden = v !== selected; });
      all('[data-explorer-view-button]', group).forEach(b => {
        b.hidden = !available.some(v => v.dataset.explorerView === b.dataset.explorerViewButton);
        b.setAttribute('aria-pressed', String(b.dataset.explorerViewButton === selected?.dataset.explorerView));
      });
      group.querySelector('.explorer-views').hidden = available.length < 2;
      group.querySelector('.explorer-overview').hidden = !available.length;
    }
    function choose(id, focus = false) {
      const panel = candidates(mode).find(p => p.dataset.explorerPanel === id);
      if (!panel) return;
      partId = id;
      panels(current).forEach(p => { p.hidden = p !== panel; });
      all('[data-explorer-part]', current).forEach(b => {
        const active = b.dataset.explorerPart === id && !b.hidden;
        b.setAttribute('aria-selected', String(active)); b.tabIndex = active ? 0 : -1;
        if (active && focus) b.focus({preventScroll:true});
      });
      all('[data-explorer-hotspot]', current).forEach(b => b.setAttribute('aria-pressed', String(b.dataset.explorerHotspot === id)));
      view(current, panel.dataset.viewId);
      const hotspot = all('[data-explorer-hotspot]', current).find(b => b.dataset.explorerHotspot === id);
      const base = viewList(current).find(v => v.dataset.explorerView === panel.dataset.viewId);
      current.querySelector('[data-explorer-location]').hidden = !base || !hotspot || hotspot.hidden;
      location(current, false);
    }
    function scene(next) {
      const items = sceneList(); sceneIndex = items.length ? (next + items.length) % items.length : 0;
      items.forEach((f,i) => { f.hidden = i !== sceneIndex; });
      root.querySelector('[data-scene-count]').textContent = items.length ? (sceneIndex + 1) + ' / ' + items.length : '';
      root.querySelector('.workbench-scene-controls').hidden = items.length < 2;
    }
    function notes() {
      all('[data-part-notes]', purchase).forEach(group => {
        const variant = groups.find(g => g.dataset.explorerVariant === group.dataset.partNotes);
        all('[data-part-note]', group).forEach(note => {
          const panel = panels(variant).find(p => p.dataset.explorerPanel === note.dataset.partNote);
          note.hidden = !!panel && usable(panel);
        });
        group.hidden = group.dataset.partNotes !== current?.dataset.explorerVariant || !all('[data-part-note]', group).some(n => !n.hidden);
      });
    }
    function setMode(next, focus = false) {
      const previous = document.activeElement;
      if (!hasMode(next)) next = 'appearance';
      if (mode === 'video' && next !== 'video') stopFilms();
      mode = next; root.dataset.explorerMode = mode;
      modes.forEach(b => {
        b.hidden = !hasMode(b.dataset.explorerMode);
        const active = b.dataset.explorerMode === mode;
        b.setAttribute('aria-selected', String(active)); b.tabIndex = active ? 0 : -1;
      });
      root.querySelector('.workbench-modes').hidden = modes.filter(b => !b.hidden).length < 2;
      all('[data-explorer-mode-panel]', root).forEach(p => { p.hidden = p.dataset.explorerModePanel !== (mode === 'storage' ? 'structure' : mode); });
      root.querySelector('[data-explorer-mode-panel="structure"]').setAttribute('aria-labelledby', modes.find(b => b.dataset.explorerMode === (mode === 'storage' ? 'storage' : 'structure')).id);
      if (current) {
        const options = candidates(mode);
        all('[data-explorer-part]', current).forEach(b => { b.hidden = !options.some(p => p.dataset.explorerPanel === b.dataset.explorerPart); });
        all('[data-explorer-hotspot]', current).forEach(b => {
          const photo = b.closest('[data-explorer-view]');
          const x = parseFloat(b.style.getPropertyValue('--x')), y = parseFloat(b.style.getPropertyValue('--y'));
          b.hidden = photo.dataset.failed === 'true' || !Number.isFinite(x) || !Number.isFinite(y) || x < 0 || x > 100 || y < 0 || y > 100 || !options.some(p => p.dataset.explorerPanel === b.dataset.explorerHotspot);
        });
        if (options.length) choose((options.find(p => p.dataset.explorerPanel === partId) || options[0]).dataset.explorerPanel);
        else { panels(current).forEach(p => { p.hidden = true; }); location(current, false); }
      }
      all('[data-explorer-feature]', root).forEach(b => { b.hidden = !panels(current).some(p => usable(p) && p.dataset.explorerPanel === b.dataset.explorerFeature); });
      root.querySelector('.workbench-feature-links').hidden = mode !== 'appearance' || !all('[data-explorer-feature]', root).some(b => !b.hidden);
      all('[data-editorial-zoom]', root).forEach(b => { b.hidden = !document.querySelector('[data-image-lightbox]') || !!b.closest('[data-failed]'); });
      notes();
      if (mode === 'scenes') scene(sceneIndex);
      if (focus) modes.find(b => b.dataset.explorerMode === mode)?.focus({preventScroll:true});
      else repairFocus(previous);
    }
    function switchVariant(id) {
      stopFilms();
      current = groups.find(g => g.dataset.explorerVariant === String(id));
      groups.forEach(g => { g.hidden = g !== current; location(g, false); });
      all('[data-explorer-scenes]', root).forEach(g => { g.hidden = g.dataset.explorerScenes !== String(id); });
      films.forEach(f => { f.hidden = f.dataset.explorerFilm !== String(id) || f.dataset.failed === 'true'; });
      sceneIndex = 0; setMode(mode);
    }
    function modeForPart(id, focus = false) {
      const p = panels(current).find(p => usable(p) && p.dataset.explorerPanel === id);
      if (p) { setMode(p.dataset.category); choose(id, focus); }
    }
    groups.forEach(group => {
      view(group);
      const buttons = all('[data-explorer-part]', group);
      buttons.forEach(b => {
        b.addEventListener('click', () => modeForPart(b.dataset.explorerPart));
        b.addEventListener('keydown', e => {
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          if (!['ArrowDown','ArrowUp','ArrowRight','ArrowLeft','Home','End'].includes(e.key)) return;
          e.preventDefault();
          const available = buttons.filter(visible), i = available.indexOf(b);
          if (!available.length) return;
          const next = e.key === 'Home' ? 0 : e.key === 'End' ? available.length - 1 : (i + (['ArrowDown','ArrowRight'].includes(e.key) ? 1 : -1) + available.length) % available.length;
          choose(available[next].dataset.explorerPart, true);
        });
      });
      all('[data-explorer-hotspot]', group).forEach(b => b.addEventListener('click', () => modeForPart(b.dataset.explorerHotspot)));
      all('[data-explorer-view-button]', group).forEach(b => b.addEventListener('click', () => {
        const next = candidates(mode).find(p => p.dataset.viewId === b.dataset.explorerViewButton);
        if (next) choose(next.dataset.explorerPanel);
        else view(group, b.dataset.explorerViewButton);
        location(group, true);
      }));
      group.querySelector('[data-explorer-location]').addEventListener('click', () => location(group, !group.classList.contains('explorer-show-location')));
    });
    modes.forEach(b => {
      b.addEventListener('click', () => setMode(b.dataset.explorerMode));
      b.addEventListener('keydown', e => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (!['ArrowRight','ArrowLeft','Home','End'].includes(e.key)) return;
        e.preventDefault();
        const available = modes.filter(visible), i = available.indexOf(b);
        if (!available.length) return;
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? available.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + available.length) % available.length;
        setMode(available[next].dataset.explorerMode, true);
      });
    });
    all('[data-explorer-feature]', root).forEach(b => b.addEventListener('click', () => modeForPart(b.dataset.explorerFeature, true)));
    root.querySelector('[data-scene-prev]').addEventListener('click', () => scene(sceneIndex - 1));
    root.querySelector('[data-scene-next]').addEventListener('click', () => scene(sceneIndex + 1));
    function expanded(value) {
      purchase.classList.toggle('workbench-expanded', value); expand.setAttribute('aria-expanded', String(value));
      root.querySelector('[data-expand-label]').textContent = english ? (value ? 'Collapse view' : 'Expand view') : (value ? 'Ansicht verkleinern' : 'Ansicht vergrößern');
      if (!value) expandFocus?.focus({preventScroll:true});
    }
    if (expand && purchase) {
      expand.hidden = false;
      expand.addEventListener('click', () => { const value = !purchase.classList.contains('workbench-expanded'); if (value) expandFocus = document.activeElement; expanded(value); });
      root.addEventListener('keydown', e => { if (e.key === 'Escape' && purchase.classList.contains('workbench-expanded')) { expanded(false); e.stopPropagation(); } });
      window.matchMedia?.('(max-width: 999px)').addEventListener('change', e => { if (e.matches) expanded(false); });
    }
    function failedImage(img) {
      const holder = img.closest('.explorer-detail-image,.explorer-view,.workbench-scene');
      if (holder) { holder.hidden = true; holder.dataset.failed = 'true'; }
      const panel = img.closest('[data-explorer-panel]');
      if (panel) panel.dataset.hasImage = 'false';
      const poster = img.closest('[data-pdp-video]');
      if (poster) { poster.dataset.failed = 'true'; poster.hidden = true; }
    }
    root.addEventListener('error', e => {
      if (e.target.tagName === 'VIDEO' || e.target.tagName === 'SOURCE') {
        const f = e.target.closest('[data-pdp-video]'); if (f) { f.dataset.failed = 'true'; stopFilms(); f.hidden = true; }
      } else if (e.target.tagName === 'IMG') failedImage(e.target);
      else return;
      setMode(mode);
    }, true);
    all('img', root).filter(img => img.complete && img.getAttribute('src') && !img.naturalWidth).forEach(failedImage);
    purchase?.addEventListener('azela:gallery-change', () => repairFocus(document.activeElement));
    document.addEventListener('azela:variant-change', e => {
      if (e.detail.main !== false && String(e.detail.productId) === root.dataset.productId) switchVariant(e.detail.variantId);
    });
    document.addEventListener('azela:quick-view', stopFilms);
    window.addEventListener('pagehide', stopFilms);
    document.addEventListener('visibilitychange', () => { if (document.hidden) stopFilms(); });
    switchVariant(current?.dataset.explorerVariant);
  }
  all('[data-product-explorer]').forEach(mount);
  document.addEventListener('shopify:section:load', e => all('[data-product-explorer]', e.target).forEach(mount));
})();
